import { createApi } from '@reduxjs/toolkit/query/react';
import { keyMasterQuery } from './config';
import { CreateApiKeyResponse } from 'src/types/keymaster/models/CreateApiKeyResponse';
import { ListApiKeysResponse } from 'src/types/keymaster/models/ListApiKeysResponse';

export interface ApiKeysRequest {
  user_id: string;
}

export const keymasterApi = createApi({
  reducerPath: 'keymasterApi',
  baseQuery: keyMasterQuery,
  endpoints: (builder) => ({
    getApiKeysList: builder.query<ListApiKeysResponse, ApiKeysRequest>({
      query: ({ user_id }) => ({
        url: `/users/${user_id}/keys`,
      }),
      merge: (currentCache, newData) => {
        if (newData.api_keys) {
          currentCache?.api_keys?.push(...newData.api_keys);
        }
      },
    }),

    createApiKey: builder.mutation<
      CreateApiKeyResponse,
      {
        user_id: string;
        keyName: string;
      }
    >({
      query: ({ user_id, keyName }) => ({
        url: `/users/${user_id}/keys`,
        method: `POST`,
        body: {
          name: keyName,
        },
      }),

      async onQueryStarted({ ...patch }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(
            keymasterApi.util.updateQueryData(
              'getApiKeysList',
              { user_id: patch.user_id },
              (cachedData) => {
                cachedData.api_keys = [
                  ...(cachedData.api_keys ?? []),
                  data.api_key,
                ];
              },
            ),
          );
        } catch {}
      },
    }),

    deleteApiKey: builder.mutation<
      { video_id: string },
      {
        user_id: string;
        api_key: string;
      }
    >({
      query: ({ user_id, api_key }) => ({
        url: `/users/${user_id}/keys/${api_key}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ user_id, api_key }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          keymasterApi.util.updateQueryData(
            'getApiKeysList',
            { user_id },
            (draft) => {
              if (!draft.api_keys) {
                return;
              }

              draft.api_keys = draft.api_keys.filter(
                (item) => item.api_key !== api_key,
              );
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export const {
  useGetApiKeysListQuery,
  useLazyGetApiKeysListQuery,
  useCreateApiKeyMutation,
  useDeleteApiKeyMutation,
} = keymasterApi;
